import { Grid } from "gridjs";
import "gridjs/dist/theme/mermaid.css";
import { Api } from './api';
import { Utils } from './utils';
import { config, Payload } from "./config";
import type { OrgConfig, ClientSummary } from "../../shared/contract";

const settings = {
    hmrcSandbox: "https://test-www.tax.service.gov.uk/oauth/authorize",
    hmrcProduction: "https://www.tax.service.gov.uk/oauth/authorize",
    redirectUri: "https%3A%2F%2Flisamanager.com"
}

export class App {
    private client: Record<string, any>;
    private clients: ClientSummary[];
    private grid: Grid;
    private logGrid: Grid;
    private api: Api;
    private orgConfig: OrgConfig | null = null;

    constructor(api: Api) {
        this.api = api;
        this.client = {};
        this.client.details = {};
        this.clients = [];
        this.logGrid = new Grid(config.grids.log);
        this.logGrid.render(Utils.getElement(config.grid_ids.log));
    }

    /** Alpine UI store driving view/visibility state. */
    private get ui() {
        return (window as any).Alpine.store('ui');
    }

    /** Alpine store backing the client life-event / transaction tables (x-for). */
    private get tables() {
        return (window as any).Alpine.store('tables');
    }

    public async event(action: string, payload?: Payload) {
        if (action.startsWith('btn-'))
            action = action.substring(4); // Remove the first 4 characters

        switch (action) {
            case 'listview':
                this.ui.view = 'grid';
                Utils.setClientMenu(false); // no client selected → disable client-scoped items
                this.client = {};
                this.client.details = {};
                break;
            case 'log':
                this.ui.view = 'log';
                const log = await this.api.getLog();
                this.logGrid.updateConfig({
                    data: log,
                }).forceRender();
                break;
            default:
                // Open a form in the overlay
                Utils.populate(action, {...this.client.details, ...payload});
                Utils.show(action);
                this.ui.activeForm = action;
        }
    }

    public info(action: string, targetId: string) {
        if (!action.startsWith('info-'))
            throw new Error ("Unexpected get action: " + action);

        const command = action.substring(5); // Remove the first 5 characters "info-"
        console.log("INFO " + command + " " + targetId, this.client);
        const targetColumn = (command == "event") ? "lifeEventId" : "transactionId";
        const record = this.client[command].find(record => record[targetColumn] === targetId);
        Utils.showInfo(command, record);
    }

    public async get(action: string, targetId: string) {
        if (!action.startsWith('get-'))
            throw new Error ("Unexpected get action: " + action);

        let command = action.substring(4); // Remove the first 4 characters "get-"
        console.log("GET " + command + " " + targetId);
        let payload: Payload = {
            investorId: this.client.details.investorId,
            accountId: this.client.details.accountId ?? null
        }
        switch (command) {
            case "viewLifeEvent":
                payload.lifeEventId = targetId;
                break;
            case "viewTransaction":
                const row = this.client.transaction.find(r => r.transactionId === targetId);
                if (row.withdrawalAmount)
                    command = "getDetailsOfWithdrawalCharge";
                else
                    command = "getDetailsOfBonusRequest";
                payload.transactionId = targetId;
                break;
        }
        const response = await this.api.lisa(command, payload);
        Utils.showInfo(command, response.data);
    }

    public async send(command: string) {
        // `command` is the form id, passed directly from the Alpine @click binding.
        const valid = Utils.validate(command);
        if (valid) {
            Utils.hide(command);  // command will be the id of the form
            this.ui.activeForm = null;

            const payload = {
                investorId: this.client.details.investorId ?? null,
                accountId: this.client.details.accountId ?? null,
                data: Utils.getValues(command)
            }
            const response = await this.api.lisa(command, payload);
            if (response.status >= 400)
                Utils.showInfo("lisa api error", response.data.errors ?? {error: response.data.message});
            else {
                switch (command) {
                    case 'getListOfAllPaymentsAndDebtsInDateRange':
                        Utils.showTable(command, response.data.payments);
                        break;
                    default:
                        this.client = response;
                        this.refreshClient();
                        await this.refreshClientsList();
                }
            }
        }
    }

    /** Re-fetch the investor list and update the main grid (e.g. after a new investor is created). */
    private async refreshClientsList() {
        this.clients = await this.api.getClients();
        if (this.grid)
            this.grid.updateConfig({ data: this.clients }).forceRender();
    }

    public async showClient(investorId: string) {
        // history.pushState({ page: "https://lisamanager.com" }, '', "https://lisamanager.com"); // more sophistication required!
        Utils.setClientMenu(true); // a client is now in view → enable client-scoped items
        this.ui.view = 'client';

        this.client = await this.api.getClient(investorId);

        Utils.populate("client", this.client.details);
        this.tables.events = this.client.event ?? [];
        this.tables.transactions = this.client.transaction ?? [];
    }

    private refreshClient() {
        Utils.populate("client", this.client.details);
        this.tables.events = this.client.event ?? [];
        this.tables.transactions = this.client.transaction ?? [];
    }

    public async initialize() {
        this.orgConfig = await this.api.getOrgConfig();
        this.clients = await this.api.getClients();
        const settings = config.grids.details;
        settings.data = this.clients,
        this.grid = new Grid(settings);

        this.grid.render(Utils.getElement(config.grid_ids.details));
        this.grid.on('rowClick', (...args) => {
            const investorId = args[1].cells[0].data as string;
            this.showClient(investorId);
        });
    }

    /**
     * Change the in-memory pagination page size for the data grids.
     * Grid.js has no built-in page-size selector, so this is driven by a custom
     * <select> in the markup and re-applies the limit to both grids.
     */
    public setPageSize(size: number | string) {
        const limit = Number(size) || 25;
        this.grid?.updateConfig({ pagination: { limit, summary: true } }).forceRender();
        this.logGrid?.updateConfig({ pagination: { limit, summary: true } }).forceRender();
    }

    public hmrcUrl(): string {
        if (!this.orgConfig)
            throw new Error("Org config not loaded — call initialize() first");
        const nonce = crypto.randomUUID();
        localStorage.setItem('hmrc_state', nonce);
        const baseUrl = this.orgConfig.env === "sandbox"
            ? settings.hmrcSandbox
            : settings.hmrcProduction;
        return baseUrl + `?response_type=code&client_id=${this.orgConfig.hmrcClientId}&scope=write:lisa%20read:lisa&state=${nonce}&redirect_uri=${settings.redirectUri}`;
    }

}
